import { SHOPIFY_DOMAIN } from "@const/urls";

import type { ICaaToken } from "@store/customer";
import { useCustomerStore } from "@store/customer";

/**
 * HIKO Social Login Plus(signin-headless)의 커스텀 엘리먼트.
 * 패키지에 공식 타입 선언(.d.ts)이 빌드에서 안 나와서(모노레포 tsconfig 충돌) 실제 사용하는
 * 메서드만 소스(src/auth.js, src/element.js) 확인해서 직접 선언.
 */
export interface IHikoSigninElement extends HTMLElement {
  isLoggedIn: () => boolean;
  login: (provider?: string | null, opts?: { method?: "email"; email?: string }) => void;
  /**
   * 0.2.0부터 브로커 세션 무효화 후 곧바로 페이지를 리다이렉트시킨다(Promise가 정상 resolve
   * 안 되는 게 일반적인 경우). Shopify 세션과 브로커의 first-party 쿠키가 cross-origin fetch로는
   * 안 지워져서 top-level navigation이 필요하다는 게 이유 — id_token이 없는 세션에서만 예외적으로
   * navigate 없이 resolve된다.
   */
  logout: () => Promise<void>;
  getToken: () => Promise<{ accessToken: string; expiresAt: number } | null>;
  getSession: () => Promise<{ loggedIn: boolean } | null>;
}

const JP_SHOP_DOMAIN = SHOPIFY_DOMAIN.jp.replace(/^https?:\/\//, "").replace(/\/$/, "");

/**
 * 클릭 시점에 어떤 방식(provider)으로 로그인을 시도했는지 sessionStorage에 잠깐 기록해두는 키.
 * 위젯이 곧바로 풀 리다이렉트로 페이지를 이동시키므로, 리다이렉트-백 후(hikoAuthSync.ts의
 * acceptHikoLogin)에 이 값을 읽어 store에 영구 반영한다(SocialLoginCaa.tsx가 기록).
 * Shopify customer에 provider가 태깅되지 않는 상태(신버전 앱)라 프론트에서 이렇게라도 잡아두는 것 —
 * 이 브라우저에서 로그인을 "시도한" 시점 기준이라 다른 기기에서의 로그인은 알 수 없는 best-effort 값.
 */
export const HIKO_LOGIN_PROVIDER_KEY = "hikoLoginProvider";

/**
 * "blocked"/"error"로 판정되어 hikoLogout()이 페이지를 리다이렉트시키기 직전에 기록해두는 값
 * ("blocked" | "error"). 로그아웃이 Shopify 로그아웃 왕복(top-level navigation)을 거치고
 * 돌아오므로, acceptHikoLogin()의 반환값(THikoLoginResult)이 호출부에 전달되지 못하고 페이지가
 * 이미 이동해버릴 수 있다 — 그 경우를 대비해 App.tsx가 부팅 시 이 값을 먼저 확인해서 모달/토스트를
 * 띄운다.
 */
export const HIKO_LOGIN_NOTICE_KEY = "hikoLoginNotice";

let sessionElement: IHikoSigninElement | null = null;

/**
 * 화면에 보이지 않는 <hiko-signin> 인스턴스를 앱 전체에서 하나만 유지.
 * 로그인 버튼(SocialLoginCaa)이 언마운트된 뒤에도 apolloCaa.ts의 refresh, 로그아웃에서
 * 위젯의 세션에 접근하기 위함.
 *
 * signin-headless의 세션 토큰(auth.js의 `token`)은 엘리먼트별 인메모리 상태이고, 리다이렉트-백
 * URL의 해시(`#hiko_session=...`)를 소비해야만 채워진다 — 영속 저장소/쿠키 기반 조회가 아니다.
 * 즉 이 싱글턴이 매 로그인 리다이렉트 후 "가장 먼저 마운트되는(그리고 유일한) <hiko-signin>
 * 엘리먼트"이기 때문에 세션을 잡아낼 수 있는 것. 같은 탭에서 새로고침 없이 계속 쓰는 동안은 이
 * 싱글턴(모듈 변수)이 살아있어 refresh/logout이 정상 동작하지만, 풀 리로드가 나면(그리고 그
 * 시점에 URL에 소비할 해시가 없으면) 새로 만들어지는 엘리먼트는 세션을 복구할 방법이 없다 —
 * caaToken이 아직 유효기간 안이라도 만료 임박에 refreshHikoCaaToken()이 실패해 로그아웃될 수 있는
 * 알려진 한계.
 */
async function getSessionElement(): Promise<IHikoSigninElement> {
  if (sessionElement) return sessionElement;

  await import("signin-headless");

  const el = document.createElement("hiko-signin") as unknown as IHikoSigninElement;
  el.setAttribute("shop", JP_SHOP_DOMAIN);
  el.style.display = "none";
  document.body.appendChild(el);

  sessionElement = el;
  return el;
}

/** ms epoch expiresAt → ICaaToken.expiresAt(ISO 문자열) 변환 */
export function toCaaToken(token: { accessToken: string; expiresAt: number }): ICaaToken {
  return {
    accessToken: token.accessToken,
    refreshToken: "",
    idToken: "",
    expiresAt: new Date(token.expiresAt).toISOString(),
    source: "hiko",
  };
}

/**
 * 만료 임박/직후 갱신용. 위젯이 내부적으로 브로커 세션(refresh_token은 Hiko가 서버사이드로 보관)을
 * 통해 새 access_token을 내려줌. 세션 자체가 끊긴 경우(getToken이 값을 못 주는 경우) 로그아웃 처리.
 */
export async function refreshHikoCaaToken(): Promise<ICaaToken | null> {
  const el = await getSessionElement();
  const token = await el.getToken();

  if (!token?.accessToken) {
    useCustomerStore.getState().logout();
    return null;
  }

  const caaToken = toCaaToken(token);
  useCustomerStore.getState().loginCaa(caaToken);
  return caaToken;
}

export async function hikoLogout(): Promise<void> {
  const el = await getSessionElement();
  await el.logout();
}

/**
 * 위젯의 브로커 세션에 로그인된 토큰이 있으면 읽기만 함(store에 반영하지 않는 순수 조회).
 * 계정 검증(탈퇴요청 등) 전에 store를 먼저 오염시키지 않기 위해 acceptHikoLogin과 분리.
 */
export async function getHikoSessionToken(): Promise<{ accessToken: string; expiresAt: number } | null> {
  const el = await getSessionElement();
  if (!el.isLoggedIn()) return null;
  return el.getToken();
}
